import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ShieldAlert } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

export default function FraudSignalsPage() {
  const { api } = useAuth();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/risk-signals?limit=50');
      setSignals(res.data);
    } catch (err) {
      console.error('Fraud signals error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="fraud-signals-page" className="space-y-6">
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <ShieldAlert size={28} className="text-pink-400" />
          Fraud Signals
        </h1>
        <p className="text-slate-400 text-sm font-manrope mt-0.5">
          AI-detected risk signals across the platform
        </p>
      </motion.div>

      <div className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden" data-testid="fraud-signals-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-manrope">
            <thead>
              <tr className="border-b border-white/10 text-xs text-slate-400 uppercase tracking-wider">
                <th className="text-left py-3 px-4">User</th>
                <th className="text-left py-3 px-4">Signal Type</th>
                <th className="text-left py-3 px-4">Severity</th>
                <th className="text-left py-3 px-4">Score</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((s, i) => (
                <tr key={s.id || i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 text-white">{s.user_name || 'Unknown'}</td>
                  <td className="py-3 px-4 text-slate-300 capitalize">{s.signal_type?.replace(/_/g, ' ')}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold ${
                      s.severity === 'high' ? 'bg-pink-500/20 text-pink-400' :
                      s.severity === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>{s.severity}</span>
                  </td>
                  <td className="py-3 px-4 font-mono text-white">{s.score}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      s.status === 'active' ? 'bg-red-500/10 text-red-400' :
                      s.status === 'investigating' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-slate-500/10 text-slate-400'
                    }`}>{s.status}</span>
                  </td>
                  <td className="py-3 px-4 text-slate-500 text-xs font-mono">{new Date(s.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {signals.length === 0 && (
          <div className="p-8 text-center text-slate-500">No fraud signals detected</div>
        )}
      </div>
    </div>
  );
}
